// React
import React, { useEffect, useState, useCallback } from 'react';

// Redux
import { connect } from 'react-redux';
import { withRoomContext } from '../../../RoomContext';
import * as settingsActions from '../../../actions/settingsActions';
import * as roomActions from '../../../actions/roomActions';
import * as requestActions from '../../../actions/requestActions';

// Component
import { firestore } from '../../../lib/firebase';

// Prop types
import PropTypes from 'prop-types';

// Message
import { FormattedMessage } from 'react-intl';

// Mui styles
import { withStyles, MuiThemeProvider, createTheme } from '@material-ui/core/styles';

// Mui core
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';

// Icon
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

// Const
import { ROOM_ONLINE } from '../../../const';

const styles = () =>
	({
		root :
		{
			zIndex : 99,
			width  : '100%',
			height : '100%'
		},
		dialogPaper :
		{
			padding : '1%'
		},
		titleIcon :
		{
			width        : '3rem',
			height       : '3rem',
			marginBottom : '1%'
		},
		titleText :
		{
			color      : '#999898',
			fontSize   : '0.9rem',
			fontWeight : 'bold'
		},
		contentText :
		{
			color    : '#999898',
			fontSize : '0.8rem'
		},
		inputBtn :
		{
			width           : '4rem',
			height          : '4rem',
			borderRadius    : '4rem',
			boxShadow       : '1px 1px 4px #777',
			backgroundColor : '#8eba14',
			color           : 'white',
			'&:hover'       :
			{
				backgroundColor : '#f50057'
			}
		},
		cancelBtn :
		{
			width           : '4rem',
			height          : '4rem',
			borderRadius    : '4rem',
			boxShadow       : '1px 1px 4px #777',
			backgroundColor : '#fff',
			color           : '#929191',
			'&:hover'       :
			{
				backgroundColor : '#f50057'
			}
		},
		input :
		{
			width        : '100%',
			minWidth     : '300px',
			marginBottom : '5%'
		},
		inputLabel :
		{
			fontSize   : '0.8rem',
			width      : '100%',
			whiteSpace : 'nowrap',
			color      : '#929191'
		},
		inputValue :
		{
			fontSize : '0.8rem',
			width    : '100%'
		},
		inputField :
		{
			backgroundColor : '#fff',
			border          : '1px solid #929191',
			borderRadius    : '0.5rem',
			padding         : '0.3rem 0.5rem',
			boxShadow       : '2px 2px 4px inset #eee',
			color           : '#999898',
			fontSize        : '0.9rem',
			width           : '100%',
			'&:focus'       :
			{
				outline : 'none'
			}
		}
	});

const theme = createTheme({
	overrides : {
		MuiDialogTitle : {
			root : {
				backgroundColor : 'transparent',
				color           : '#999898',
				width           : '100%',
				padding         : 0
			}
		},
		MuiDialogContent : {
			root : {
				justifyContent : 'center',
				display        : 'flex',
				flexDirection  : 'column',
				alignItems     : 'center',
				overflowY      : 'none'
			}
		},
		MuiDialogActions : {
			root : {
				justifyContent : 'space-around',
				padding        : '0 20%'
			}
		},
		MuiPaper : {
			rounded : {
				borderRadius : '0.5rem'
			}
		}
	}
});

const HomeJoinDialog = ({
	roomClient,
	homeJoinDialogOpen,
	setHomeJoinDialogOpen,
	mediaPerms,
	classes,
	setAudioMuted,
	setVideoMuted,
	roomContext,
	showAlert,
	changeDisplayName
}) =>
{
	const [ userName, setUserName ] = useState('');
	const [ userPassword, setUserPassword ] = useState('');
	const [ disablePassword, setDisablePassword ] = useState(true);
	const [ displayName, setDisplayName ] = useState('');

	const handleClosePopup = () =>
	{
		setHomeJoinDialogOpen(false);
	};

	const handleChangeUserName = (e) =>
	{
		setUserName(e.target.value);
	};

	const handleChangeUserPassword = (e) =>
	{
		setUserPassword(e.target.value);
	};

	const handleChangeDisplayName = (e) =>
	{
		setDisplayName(e.target.value);
	};

	const askForPerms = useCallback(() =>
	{
		if (mediaPerms.video || mediaPerms.audio)
		{
			navigator.mediaDevices.getUserMedia(mediaPerms);
		}
	}, [ mediaPerms ]);

	const joinRoom = () =>
	{
		if (userName === '')
		{
			showAlert('利用者名を入力してください');

			return;
		}

		if (!disablePassword && userPassword !== roomContext.password)
		{
			showAlert('パスワードが違います');

			return;
		}

		if (displayName === '')
		{
			showAlert('表示名を入力してください');

			return;
		}

		setAudioMuted(false);

		setVideoMuted(false);

		askForPerms();

		const parts = window.location.hostname.split('.');
		const subdomain = parts.shift();
		const encodedRoomId = encodeURIComponent(roomContext.id);

		changeDisplayName(displayName);

		// ステータスを待機中に設定
		firestore.collection('rooms').doc(roomContext.id)
			.update({
				status     : ROOM_ONLINE,
				user       : userName,
				forceLeave : false
			})
			.then(() =>
			{
				roomClient.join({
					roomId    : encodedRoomId,
					joinVideo : mediaPerms.video,
					joinAudio : mediaPerms.audio,
					subdomain : subdomain
				});
				setHomeJoinDialogOpen(false);
			})
			.catch((e) =>
			{
				console.error(e); // eslint-disable-line no-console
			});
	};

	useEffect(() =>
	{
		if (roomContext)
		{
			setDisablePassword(roomContext.password === '');
		}
	}, [ roomContext ]);

	return (
		<MuiThemeProvider theme={theme}>
			<Dialog
				className={classes.root}
				open={homeJoinDialogOpen}
				onClose={handleClosePopup}
				classes={{
					paper : classes.dialogPaper
				}}
			>
				<DialogTitle id='form-dialog-title'>
					<Box
						display='flex'
						alignItems='center'
						flexDirection='column'
					>
						<ErrorOutlineIcon className={classes.titleIcon} />
						<Typography className={classes.titleText}>
							入室
						</Typography>
					</Box>
				</DialogTitle>
				<DialogContent>
					<div className={classes.input}>
						<div className={classes.inputLabel}>利用者名</div>
						<div className={classes.inputValue}>
							<input value={userName} className={classes.inputField} onChange={(value) => handleChangeUserName(value)} type='text' />
						</div>
					</div>
					<div className={classes.input}>
						<div className={classes.inputLabel}>表示名</div>
						<div className={classes.inputValue}>
							<input value={displayName} className={classes.inputField} onChange={(value) => handleChangeDisplayName(value)} type='text' />
						</div>
					</div>
					<div className={classes.input}>
						<div className={classes.inputLabel}>パスワード</div>
						<div className={classes.inputValue}>
							<input value={userPassword} className={classes.inputField} onChange={(value) => handleChangeUserPassword(value)} type='text' disabled={disablePassword}/>
						</div>
					</div>
				</DialogContent>
				<DialogActions>
					<Button
						className={classes.cancelBtn}
						onClick={handleClosePopup}
						color='primary'
					>
						<FormattedMessage
							id='label.close'
							defaultMessage='Close'
						/>
					</Button>
					<Button
						className={classes.inputBtn}
						onClick={joinRoom}
						color='default'
					>
						<FormattedMessage
							id='label.join'
							defaultMessage='Join'
						/>
					</Button>
				</DialogActions>
			</Dialog>
		</MuiThemeProvider>
	);
};

HomeJoinDialog.propTypes =
{
	roomClient            : PropTypes.any.isRequired,
	room                  : PropTypes.object.isRequired,
	homeJoinDialogOpen    : PropTypes.bool.isRequired,
	setHomeJoinDialogOpen : PropTypes.func.isRequired,
	setMediaPerms         : PropTypes.func.isRequired,
	classes               : PropTypes.object.isRequired,
	mediaPerms            : PropTypes.object.isRequired,
	setAudioMuted         : PropTypes.func.isRequired,
	setVideoMuted         : PropTypes.func.isRequired,
	roomContext           : PropTypes.object.isRequired,
	showAlert             : PropTypes.func.isRequired,
	changeDisplayName     : PropTypes.func.isRequired
};

const makeMapStateToProps = () =>
{
	const mapStateToProps = (state) =>
	{
		return {
			room               : state.room,
			mediaPerms         : state.settings.mediaPerms,
			homeJoinDialogOpen : state.room.homeJoinDialogOpen,
			changeDisplayName  : PropTypes.func.isRequired
		};
	};

	return mapStateToProps;
};

const mapDispatchToProps = (dispatch) =>
{
	return {
		setHomeJoinDialogOpen : (homeJoinDialogOpen) =>
		{
			dispatch(roomActions.setHomeJoinDialogOpen(homeJoinDialogOpen));
		},
		setMediaPerms : (mediaPerms) =>
		{
			dispatch(settingsActions.setMediaPerms(mediaPerms));
		},
		setAudioMuted : (flag) =>
		{
			dispatch(settingsActions.setAudioMuted(flag));
		},
		setVideoMuted : (flag) =>
		{
			dispatch(settingsActions.setVideoMuted(flag));
		},
		showAlert : (message) =>
		{
			dispatch(requestActions.notify({ text: message }));
		},
		changeDisplayName : (displayName) =>
		{
			dispatch(settingsActions.setDisplayName(displayName));
		}
	};
};

export default withRoomContext(connect(
	makeMapStateToProps,
	mapDispatchToProps,
	null,
	{
		areStatesEqual : (next, prev) =>
		{
			return (
				prev.room.homeJoinDialogOpen === next.room.homeJoinDialogOpen &&
				prev.settings === next.settings
			);
		}
	}
)(withStyles(styles)(HomeJoinDialog)));